import { getToken, getCompany } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";
import type { ChangeDetails } from "../../../../shared/utils/reviewWorkflow";
import type { BulkUploadApiResponseDto } from "../dto/BulkUploadDto";

const VALID_EXTENSIONS = [".xlsx", ".xls"];

function assertExcelFile(file: File): void {
  const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
  if (!VALID_EXTENSIONS.includes(ext)) {
    throw new Error("Only Excel files (.xlsx, .xls) are allowed");
  }
}

function assertCompany(): { companyGstEnabled: string } {
  const company = getCompany();
  if (!company) {
    throw new Error("Company data not found");
  }
  return { companyGstEnabled: company.companyGstEnabled || "0" };
}

async function downloadTemplate(path: string, fallbackFilename: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { message?: string }).message ||
        `Failed to download template: ${response.statusText}`,
    );
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = fallbackFilename;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

async function postUpload(path: string, file: File): Promise<BulkUploadApiResponseDto> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  assertExcelFile(file);
  const { companyGstEnabled } = assertCompany();

  const formData = new FormData();
  formData.append("file", file);
  formData.append("company_gst_enabled", companyGstEnabled);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const data = (await response.json()) as BulkUploadApiResponseDto;
  if (!response.ok) {
    throw new Error(data.message || `Upload failed: ${response.statusText}`);
  }
  return data;
}

/** Per-row validation failure from verify-for-review (HTTP 422). */
export interface BulkVerifyForReviewFailure {
  excel_row: number;
  display_name?: string;
  errors: Array<{ field: string; message: string }>;
}

export class BulkVerifyForReviewError extends Error {
  readonly invalidRows: BulkVerifyForReviewFailure[];
  /** @deprecated Use invalidRows — kept for backward compatibility with single-row responses. */
  readonly failure: BulkVerifyForReviewFailure | null;

  constructor(
    message: string,
    invalidRows: BulkVerifyForReviewFailure[],
  ) {
    super(message);
    this.name = "BulkVerifyForReviewError";
    this.invalidRows = invalidRows;
    this.failure = invalidRows[0] ?? null;
  }
}

interface VerifyBulkReviewApiResponse {
  success: boolean;
  message: string;
  data?: {
    change_details?: ChangeDetails;
    meta?: unknown;
    invalid_rows?: BulkVerifyForReviewFailure[];
    failure?: BulkVerifyForReviewFailure | null;
  };
}

async function postVerifyForReview(
  path: string,
  file: File,
): Promise<ChangeDetails> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  assertExcelFile(file);
  const { companyGstEnabled } = assertCompany();

  const formData = new FormData();
  formData.append("file", file);
  formData.append("company_gst_enabled", companyGstEnabled);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const body = (await response.json()) as VerifyBulkReviewApiResponse;
  if (!response.ok || !body.success) {
    const invalidRows =
      body.data?.invalid_rows ??
      (body.data?.failure ? [body.data.failure] : []);
    throw new BulkVerifyForReviewError(
      body.message || `Verification failed: some rows are not valid`,
      invalidRows,
    );
  }
  const cd = body.data?.change_details;
  if (!cd) {
    throw new Error("Server did not return change_details");
  }
  return cd;
}

/** Validate customers Excel for bulk review (no DB/Zoho writes). */
export const verifyCustomersBulkForReview = async (
  file: File,
): Promise<ChangeDetails> => {
  return postVerifyForReview(
    "/api/customer-vendor/upload/customers/verify-for-review",
    file,
  );
};

/** Validate vendors Excel for bulk review (no DB/Zoho writes). */
export const verifyVendorsBulkForReview = async (
  file: File,
): Promise<ChangeDetails> => {
  return postVerifyForReview(
    "/api/customer-vendor/upload/vendors/verify-for-review",
    file,
  );
};

export const downloadCustomersTemplate = async (): Promise<void> => {
  await downloadTemplate("/api/customer-vendor/template/customers", "customers_template.xlsx");
};

export const downloadVendorsTemplate = async (): Promise<void> => {
  await downloadTemplate("/api/customer-vendor/template/vendors", "vendors_template.xlsx");
};

export const uploadCustomersFile = async (file: File): Promise<BulkUploadApiResponseDto> => {
  return postUpload("/api/customer-vendor/upload/customers", file);
};

export const uploadVendorsFile = async (file: File): Promise<BulkUploadApiResponseDto> => {
  return postUpload("/api/customer-vendor/upload/vendors", file);
};

export interface ApplyBulkReviewApiResponse {
  success: boolean;
  message: string;
  data?: {
    already_applied?: boolean;
    inserted_count?: number;
    inserted_records?: unknown[];
  };
}

async function postApplyBulkReview(
  path: string,
  reviewId: number,
): Promise<ApplyBulkReviewApiResponse> {
  const token = getToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ review_id: reviewId }),
  });

  const body = (await response.json()) as ApplyBulkReviewApiResponse;
  if (!response.ok || !body.success) {
    throw new Error(body.message || `Apply failed: ${response.statusText}`);
  }
  return body;
}

/** Reviewer: apply staged customer bulk import before approving the review. */
export const applyCustomerBulkReview = async (
  reviewId: number,
): Promise<ApplyBulkReviewApiResponse> => {
  return postApplyBulkReview(
    "/api/customer-vendor/bulk-review/apply/customers",
    reviewId,
  );
};

/** Reviewer: apply staged vendor bulk import before approving the review. */
export const applyVendorBulkReview = async (
  reviewId: number,
): Promise<ApplyBulkReviewApiResponse> => {
  return postApplyBulkReview(
    "/api/customer-vendor/bulk-review/apply/vendors",
    reviewId,
  );
};
